import React, { useMemo } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format } from "date-fns";

const Trends = ({ userData, selectedStartDate, selectedEndDate }) => {
  // Process real userData to generate trends data
  const trendsData = useMemo(() => {
    if (!userData || userData.length === 0) {
      // Return mock data if no user data available
      return [
        { date: 'Feb 01', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 02', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 03', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 04', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 05', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 22', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 23', registrations: "N.A.", avgTime: "N.A." },
        { date: 'Feb 24', registrations: "N.A.", avgTime: "N.A." },
      ];
    }

    // Filter data by date range if provided
    let filteredData = userData;
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      filteredData = userData.filter((user) => {
        const userDate = new Date(user.saveDate * 1000);
        const userDateTime = userDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return userDateTime >= startDateTime && userDateTime <= endDateTime;
      });
    }

    // Group by date and count registrations
    const dailyData = {};
    
    filteredData.forEach((user) => {
      if (user.saveDate) {
        const date = new Date(user.saveDate * 1000);
        const dateKey = format(date, "MMM dd");
        
        if (dailyData[dateKey]) {
          dailyData[dateKey].registrations++;
        } else {
          dailyData[dateKey] = {
            date: dateKey,
            registrations: 1,
            // Mock connection time data - in real app this would come from connection analytics
            avgTime: "N.A." // N.A. for unavailable data
          };
        }
      }
    });

    // Convert to array format for chart and sort by date
    return Object.values(dailyData).sort((a, b) => {
      const dateA = new Date(a.date + " 2024");
      const dateB = new Date(b.date + " 2024");
      return dateA - dateB;
    });
  }, [userData, selectedStartDate, selectedEndDate]);

  // Custom tooltip
  const renderTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#1a1b23",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "12px",
            color: "white",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontWeight: "bold", color: "#24b8e7" }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: "2px 0", color: entry.color }}>
              {entry.dataKey === "registrations" 
                ? `Registrations: ${entry.value}` 
                : `Avg Time: ${entry.value === "N.A." ? "N.A." : entry.value.toFixed(1) + " min"}`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Box>
      <Card
        sx={{
          backgroundColor: "#1a1b23",
          border: "1px solid #333",
          height: "500px",
        }}
      >
        <CardContent sx={{ height: "100%", position: "relative" }}>
          <Typography variant="h5" sx={{ color: "white", mb: 1, fontSize: "24px", fontWeight: "600" }}>
            User Engagement Trends
          </Typography>
          <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 4, fontSize: "14px" }}>
            Combined view of registrations and connection time
          </Typography>
          
          {/* Line Chart using Recharts */}
          <ResponsiveContainer width="100%" height="85%">
            <LineChart
              data={trendsData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#333" 
                opacity={0.3}
                horizontal={true}
                vertical={false}
              />
              <XAxis 
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#9e9e9e", fontSize: 12 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#9e9e9e", fontSize: 12 }}
              />
              <Tooltip content={renderTooltip} />
              
              {/* Registrations trend line */}
              <Line 
                type="monotone" 
                dataKey="registrations" 
                stroke="#24b8e7" 
                strokeWidth={3}
                dot={{ 
                  fill: "#24b8e7", 
                  strokeWidth: 2, 
                  r: 4 
                }}
                activeDot={{ 
                  r: 6, 
                  fill: "#24b8e7",
                  strokeWidth: 2,
                  stroke: "#1a1b23"
                }}
                name="Registrations"
              />
              
              {/* Average time trend line */}
              <Line 
                type="monotone" 
                dataKey="avgTime" 
                stroke="#4285F4" 
                strokeWidth={3}
                dot={{ 
                  fill: "#4285F4", 
                  strokeWidth: 2, 
                  r: 4 
                }}
                activeDot={{ 
                  r: 6, 
                  fill: "#4285F4",
                  strokeWidth: 2,
                  stroke: "#1a1b23"
                }}
                name="Avg Time (min)"
              />
            </LineChart>
          </ResponsiveContainer>
          
          {/* Show message if no data */}
          {trendsData.length === 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                color: "#9e9e9e",
              }}
            >
              <Typography variant="body2">
                No trends data available for the selected period
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Trends;